import React from 'react';
import { Section } from './ui/Section';
import { motion } from 'framer-motion';
import { AlertCircle, Brain, RefreshCw } from 'lucide-react';
export function ProblemNarrative() {
  return <>
      <Section className="bg-white" data-id="element-88">
        <div className="prose prose-lg prose-slate max-w-none" data-id="element-89">
          <p className="text-xl md:text-2xl font-serif text-[#0f4c3a] leading-relaxed mb-8" data-id="element-90">
            Te pagan… y en vez de alivio sientes tensión. Como si el dinero
            entrara a tu vida solo para salir corriendo.
          </p>

          <p className="mb-6" data-id="element-91">
            Y antes de que te culpes otra vez, déjame decirte algo que te va a
            liberar:
          </p>

          <div className="bg-[#f4e5c3]/20 border-l-4 border-[#d4af37] p-6 my-8 rounded-r-lg" data-id="element-92">
            <p className="font-semibold text-[#0f4c3a] italic m-0" data-id="element-93">
              No es que seas "mala con el dinero". Es que cuando llega el
              estrés, el cansancio o la presión… tu chip decide por ti.
            </p>
          </div>

          <p className="mb-6" data-id="element-94">Por eso se repite la misma película:</p>

          <ul className="space-y-4 list-none pl-0 mb-12" data-id="element-95">
            {['Llega la quincena… y dura menos de lo que debería.', 'Te organizas 2–3 días… y luego todo se rompe.', "Te prometes 'esta vez sí'… y vuelves al mismo punto.", "Ves historias de mujeres que prosperan… y algo dentro de ti se pregunta: '¿Por qué ellas sí y yo no?'"].map((item, i) => <motion.li key={i} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="flex items-start gap-3" data-id="element-96">
                <RefreshCw className="w-5 h-5 text-[#d4af37] mt-1 flex-shrink-0" data-id="element-97" />
                <span data-id="element-98">{item}</span>
              </motion.li>)}
          </ul>

          <p className="font-medium text-[#0f4c3a]" data-id="element-99">
            Si te reconociste, sigue leyendo. Porque esto no se arregla con un
            tip suelto.
          </p>
        </div>
      </Section>

      <Section dark className="text-center" data-id="element-100">
        <h2 className="text-3xl md:text-4xl font-serif text-[#d4af37] mb-8" data-id="element-101">
          La verdad incómoda (pero liberadora)
        </h2>

        <div className="max-w-2xl mx-auto mb-12" data-id="element-102">
          <p className="text-xl mb-6 text-white" data-id="element-103">Te instalaron un chip.</p>
          <p className="text-white mb-8" data-id="element-104">
            Un chip hecho de cultura, familia, frases repetidas, miedos
            heredados… y supervivencia. Un chip que suena así:
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto text-left" data-id="element-105">
          {['Mejor pobre pero honrado.', 'El dinero no compra la felicidad.', 'Los ricos son malos.', 'Pedir más es ser ambiciosa.', 'Con lo que hay, alcanza.', 'Si me va bien, me van a envidiar.', 'Mejor no llamar la atención…'].map((phrase, i) => <motion.div key={i} whileHover={{
          scale: 1.02
        }} className="bg-white/5 border border-white/10 p-4 rounded-lg backdrop-blur-sm" data-id="element-106">
              <p className="text-[#f4e5c3] italic" data-id="element-107">"{phrase}"</p>
            </motion.div>)}
        </div>

        <div className="mt-16 max-w-2xl mx-auto" data-id="element-108">
          <p className="text-lg mb-4 text-white" data-id="element-109">
            Y aunque tu mente diga: "yo ya no creo eso"…{' '}
            <span className="text-[#d4af37] font-bold" data-id="element-110">tu cuerpo sí.</span>
          </p>
          <p className="text-lg text-white" data-id="element-111">
            Porque el dinero no se maneja solo con números. <br data-id="element-112" />
            <span className="text-2xl font-serif text-[#d4af37] block mt-4" data-id="element-113">
              El dinero se maneja con identidad.
            </span>
          </p>
        </div>
      </Section>

      <Section data-id="element-114">
        <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-115">
          <div data-id="element-116">
            <h3 className="text-2xl md:text-3xl font-serif text-[#0f4c3a] mb-6" data-id="element-117">
              El problema real: no es que no ganes… es que no sostienes
            </h3>
            <p className="text-slate-600 mb-4" data-id="element-118">
              A muchas mujeres les pasa exactamente esto:
            </p>
            <p className="text-slate-600 mb-6" data-id="element-119">
              Pueden ser inteligentes. Pueden ser trabajadoras. Pueden incluso
              generar ingresos extra… Pero el dinero no se queda.
            </p>
            <div className="bg-red-50 border-l-4 border-red-200 p-4 mb-6" data-id="element-120">
              <p className="text-red-800 italic m-0" data-id="element-121">
                "Es que yo soy mala con el dinero."
                <br data-id="element-122" />
                "Yo no tengo disciplina."
                <br data-id="element-123" />
                "Yo no nací para esto."
              </p>
            </div>
            <p className="font-bold text-[#0f4c3a]" data-id="element-124">Escúchame bien:</p>
            <p className="text-slate-600" data-id="element-125">
              Ganar dinero es una habilidad. Retener y multiplicar es otra.
            </p>
          </div>

          <div className="bg-[#f8f9fa] p-8 rounded-2xl border border-slate-100 shadow-sm" data-id="element-126">
            <h4 className="font-serif text-xl text-[#0f4c3a] mb-6" data-id="element-127">
              Esa segunda habilidad depende de 3 capas:
            </h4>
            <ul className="space-y-6" data-id="element-128">
              {[{
              title: 'Lo que crees del dinero',
              desc: '(merecimiento, culpa, miedo, identidad).'
            }, {
              title: 'Lo que sientes',
              desc: 'cuando el dinero aparece o falta (ansiedad, impulso, tensión).'
            }, {
              title: 'Lo que haces en la práctica',
              desc: '(sistema, método, hábitos).'
            }].map((layer, i) => <li key={i} className="flex gap-4" data-id="element-129">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#d4af37] text-[#0f4c3a] flex items-center justify-center font-bold" data-id="element-130">
                    {i + 1}
                  </div>
                  <div data-id="element-131">
                    <strong className="block text-[#0f4c3a]" data-id="element-132">
                      {layer.title}
                    </strong>
                    <span className="text-slate-600 text-sm" data-id="element-133">{layer.desc}</span>
                  </div>
                </li>)}
            </ul>
          </div>
        </div>

        <div className="mt-8 text-center max-w-2xl mx-auto" data-id="element-134">
          <p className="text-lg text-slate-700 mb-8" data-id="element-135">
            La mayoría de soluciones solo toca una capa. Finanzas sin trabajar
            culpa/impulso → se rompe cuando hay estrés. Manifestación sin
            estructura → se queda en deseo. Motivación sin sistema → dura 3
            días.
          </p>
          <p className="text-xl font-serif text-[#0f4c3a]" data-id="element-136">
            Resultado: empiezas fuerte… y se cae. Y tú te culpas. Pero no es tu
            culpa.{' '}
            <span className="text-[#d4af37] font-bold" data-id="element-137">
              Es un método incompleto.
            </span>
          </p>
        </div>
      </Section>

      {/* Metaphor 1: Bucket */}
      <Section className="bg-[#f0f7f4]" data-id="element-138">
        <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-139">
          <div className="flex-1 order-2 md:order-1" data-id="element-140">
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-[#0f4c3a]/10 relative overflow-hidden" data-id="element-141">
              <div className="absolute top-0 right-0 w-24 h-24 bg-[#d4af37]/10 rounded-bl-full -mr-4 -mt-4" data-id="element-142" />

              <h3 className="text-2xl font-serif text-[#0f4c3a] mb-4" data-id="element-143">
                Metáfora 1: el balde con agujeros
              </h3>
              <p className="text-slate-600 mb-4" data-id="element-144">
                Imagina que tu dinero es agua. Y tú tienes un balde. La mayoría
                hace lo obvio: echar más agua.
              </p>
              <p className="text-slate-600 mb-6 font-medium" data-id="element-145">
                Más trabajo. Más clientes. Más ingresos. Más "rebusque". Más
                esfuerzo. Pero… ¿qué pasa si tu balde tiene agujeros?
              </p>
              <p className="text-xl font-serif text-[#d4af37] text-center my-6" data-id="element-146">
                Se va igual.
              </p>

              <p className="text-slate-600 mb-4" data-id="element-147">
                Y los agujeros casi nunca son "falta de ganas". Son invisibles:
              </p>
              <ul className="space-y-2 text-sm text-slate-600" data-id="element-148">
                <li className="flex items-center gap-2" data-id="element-149">
                  <AlertCircle className="w-4 h-4 text-red-400" data-id="element-150" /> culpa por
                  querer más
                </li>
                <li className="flex items-center gap-2" data-id="element-151">
                  <AlertCircle className="w-4 h-4 text-red-400" data-id="element-152" /> miedo a
                  prosperar
                </li>
                <li className="flex items-center gap-2" data-id="element-153">
                  <AlertCircle className="w-4 h-4 text-red-400" data-id="element-154" /> impulsos de
                  compensación
                </li>
                <li className="flex items-center gap-2" data-id="element-155">
                  <AlertCircle className="w-4 h-4 text-red-400" data-id="element-156" /> caos mental
                </li>
                <li className="flex items-center gap-2" data-id="element-157">
                  <AlertCircle className="w-4 h-4 text-red-400" data-id="element-158" /> identidad de
                  escasez
                </li>
              </ul>
            </div>
          </div>

          <div className="flex-1 order-1 md:order-2 text-center md:text-left" data-id="element-159">
            <h2 className="text-3xl md:text-4xl font-serif text-[#0f4c3a] mb-6" data-id="element-160">
              Hasta que tapes esos agujeros, no importa cuánto entre.
            </h2>
            <p className="text-lg text-slate-700 mb-8" data-id="element-161">
              Y aquí viene lo mejor: Sí se pueden tapar. Sin volverte otra
              persona. Sin "ser perfecta". Sin depender de motivación.
            </p>
            <div className="inline-block border-b-2 border-[#d4af37] pb-1" data-id="element-162">
              <p className="text-xl font-medium text-[#0f4c3a]" data-id="element-163">
                Con un método completo.
              </p>
            </div>
          </div>
        </div>
      </Section>
    </>;
}